/* Intent card — what the AI thinks the customer is calling about.
 *
 * Reads detected_intent + intent_confidence from the orchestrator state
 * (bot writes them on the first user turn). Lets the agent override
 * with an inline picker; the override PATCHes /api/calls/:id and bumps
 * intent_overridden_at so the badge can read "✎ overridden by agent". */

import { useState } from "react";

import { API_URL } from "../lib/api";
import { agentHeaders, useAgentIdentity } from "./identity";
import { type Intent } from "./useCallStage";

const INTENT_LABEL: Record<Intent, string> = {
  bill_shock:        "Bill shock",
  competitor_switch: "Competitor switch",
  service_issue:     "Service issue",
  cancel_no_save:    "Cancel — no save",
  other:             "Other",
};

const INTENT_OPTIONS: Intent[] = [
  "bill_shock",
  "competitor_switch",
  "service_issue",
  "cancel_no_save",
  "other",
];

function confidenceTier(confidence: number | null): "low" | "mid" | "high" | "none" {
  if (confidence === null) return "none";
  if (confidence < 0.5) return "low";
  if (confidence < 0.8) return "mid";
  return "high";
}

function confidenceLabel(confidence: number | null): string {
  if (confidence === null) return "—";
  return `${Math.round(confidence * 100)}%`;
}

export function IntentCard({
  callId,
  intent,
  confidence,
  overriddenAt,
}: {
  callId: number;
  intent: Intent | null;
  confidence: number | null;
  overriddenAt: string | null;
}) {
  const { identity } = useAgentIdentity();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const isOverridden = overriddenAt !== null;
  const displayLabel = intent ? INTENT_LABEL[intent] : "Classifying…";
  const tier = confidenceTier(confidence);

  async function pickIntent(next: Intent) {
    setBusy(true);
    setErr(null);
    try {
      const r = await fetch(`${API_URL}/api/calls/${callId}`, {
        method: "PATCH",
        headers: {
          "content-type": "application/json",
          ...agentHeaders(identity),
        },
        body: JSON.stringify({
          detected_intent: next,
          intent_overridden_at: new Date().toISOString(),
        }),
      });
      if (!r.ok) {
        const text = await r.text().catch(() => "");
        setErr(text || `override failed (${r.status})`);
        return;
      }
      // Live-stream will catch up the row, but close the picker eagerly
      // so the agent doesn't have to wait for SSE round-trip.
      setEditing(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <article id="agent-desk-intent-card" className="agent-desk-intent-card">
      <div className="agent-desk-intent-head">
        <div className="agent-desk-intent-eyebrow">DETECTED INTENT</div>
        {!editing && intent !== null && (
          <button
            id="agent-desk-intent-override-btn"
            type="button"
            className="agent-desk-intent-override-btn"
            onClick={() => setEditing(true)}
            disabled={busy}
            title="Override intent"
          >
            ✎ Change
          </button>
        )}
      </div>

      <div className="agent-desk-intent-body">
        <span
          id="agent-desk-intent-value"
          className={`agent-desk-intent-value tier-${tier}`}
        >
          {displayLabel}
        </span>
        {intent !== null && (
          <span
            id="agent-desk-intent-confidence"
            className={`agent-desk-intent-confidence tier-${tier}`}
            title="Classifier confidence"
          >
            {confidenceLabel(confidence)} confidence
          </span>
        )}
        {isOverridden && (
          <span
            id="agent-desk-intent-override-badge"
            className="agent-desk-intent-override-badge"
            title={`Overridden by agent at ${overriddenAt ?? ""}`}
          >
            ✎ overridden
          </span>
        )}
      </div>

      {editing && (
        <div id="agent-desk-intent-picker" className="agent-desk-intent-picker">
          <div className="agent-desk-intent-picker-label">
            Set the intent the system should use:
          </div>
          <div className="agent-desk-intent-picker-options">
            {INTENT_OPTIONS.map((opt) => (
              <button
                key={opt}
                id={`agent-desk-intent-pick-${opt}`}
                type="button"
                className={`agent-desk-intent-pick ${
                  opt === intent ? "is-current" : ""
                }`}
                onClick={() => pickIntent(opt)}
                disabled={busy}
              >
                {INTENT_LABEL[opt]}
              </button>
            ))}
          </div>
          <div className="agent-desk-intent-picker-foot">
            <button
              id="agent-desk-intent-cancel-btn"
              type="button"
              className="agent-desk-intent-cancel"
              onClick={() => setEditing(false)}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
          {err && <div className="dial-err">{err}</div>}
        </div>
      )}
    </article>
  );
}
